import React from 'react';
import {
  Heart, ChevronRight, Star, GraduationCap, Activity, Users,
  ArrowRight, Quote
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useCounter from '../hooks/useCounter';
import NewsletterSection from '../components/NewsletterSection';

const HomePage = () => {
  const navigate = useNavigate();
  const [livesCount, livesRef] = useCounter(1200);
  const [programmesCount, programmesRef] = useCounter(12);
  const [volunteersCount, volunteersRef] = useCounter(85);
  const [communitiesCount, communitiesRef] = useCounter(8);

  return (
    <>
      {/* Hero */}
      <section className="hero">
        <div className="hero-bg-pattern" />
        {/* <div className="hero-float-shape" />
        <div className="hero-float-shape" />
        <div className="hero-float-shape" /> */}

        <div className="container" style={{ display: 'grid', gridTemplateColumns: '1fr', alignItems: 'center', gap: '3rem' }}>
          <div className="hero-content">
            <div className="hero-badge">
              <Star size={14} />
              Registered NPO &bull; 2021/766891/08
            </div>
            <h1>
              Empowering Communities Through{' '}
              <span className="highlight">Love & Action</span>
            </h1>
            <p>
              Realm of Love Foundation transforms lives across South Africa through education,
              wellness programmes, and sustainable community development initiatives.
            </p>
            <div className="hero-buttons">
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/donate')}>
                <Heart size={18} /> Donate Now
              </button>
              <button className="btn btn-outline-light btn-lg" onClick={() => navigate('/programmes')}>
                Our Programmes <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Strip */}
      <section style={{ background: 'var(--off-white)', padding: '4rem 0' }}>
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Our Mission</span>
            <h2>Building Stronger Communities Together</h2>
            <p>Through education, health initiatives, and community empowerment, we create lasting change where it matters most.</p>
          </div>
          <div className="grid grid-3">
            {[
              { icon: <GraduationCap size={40} />, title: 'Education Access', desc: 'Providing quality education and skills training to underserved communities.', color: 'var(--teal)' },
              { icon: <Activity size={40} />, title: 'Health & Wellness', desc: 'Promoting physical and mental wellbeing through targeted health programmes.', color: 'var(--rose)' },
              { icon: <Users size={40} />, title: 'Community Building', desc: 'Strengthening communities through sustainable development and empowerment.', color: 'var(--gold)' }
            ].map((item, i) => (
              <div key={i} className="scroll-animate card" style={{ textAlign: 'center', padding: '2.5rem 2rem', transitionDelay: `${i * 150}ms` }}>
                <div style={{
                  width: '80px', height: '80px', borderRadius: '16px',
                  background: `${item.color}15`, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', margin: '0 auto 1.25rem', color: item.color
                }}>
                  {item.icon}
                </div>
                <h3 style={{ fontSize: '1.25rem', marginBottom: '0.75rem' }}>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Programmes */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">What We Do</span>
            <h2>Our Key Programmes</h2>
            <p>Comprehensive initiatives designed to uplift and empower at every level.</p>
          </div>
          <div className="grid grid-2">
            {[
              { title: 'Adopt-a-School Impact Programme', cat: 'Education', catClass: 'cat-education', desc: 'Long-term partnerships with selected public schools to deliver sustained, measurable impact for learners and families in under-resourced communities.', image: '/img/DSC_1916.jpg' },
              { title: 'Healthier Future Community Programme', cat: 'Wellness', catClass: 'cat-wellness', desc: 'Our flagship initiative improving the nutritional, physical, mental, and emotional wellbeing of individuals and families through community-wide wellness workshops.', image: '/img/wellness.jpg' },
              { title: 'Back-to-School Essentials Drive', cat: 'Community', catClass: 'cat-community', desc: 'An annual drive providing school bags, stationery, and essential items to learners from less-privileged backgrounds, ensuring dignity and equality in the classroom.', image: '/img/excellence.jpg' },
              { title: 'Power of Reading Initiative', cat: 'Education', catClass: 'cat-education', desc: 'A literacy programme nurturing a love for reading among early-grade learners through guided sessions and age-appropriate materials to build foundational skills.', image: '/img/image_2.jpg' }
            ].map((prog, i) => (
              <div key={i} className="scroll-animate programme-card" style={{ transitionDelay: `${i * 100}ms` }}>
                <div style={{
                  height: '160px', overflow: 'hidden'
                }}>
                  <img src={prog.image} alt={prog.title} style={{
                    width: '100%', height: '100%', objectFit: 'cover'
                  }} />
                </div>
                <div className="programme-card-body">
                  <span className={`programme-card-category ${prog.catClass}`}>{prog.cat}</span>
                  <h4 style={{ marginBottom: '0.5rem' }}>{prog.title}</h4>
                  <p style={{ fontSize: '0.875rem' }}>{prog.desc}</p>
                  <button
                    onClick={() => navigate('/programmes')}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                      marginTop: '1rem', color: 'var(--teal)', fontWeight: 600, fontSize: '0.875rem',
                      background: 'none', border: 'none', cursor: 'pointer'
                    }}
                  >
                    Learn more <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section style={{ background: 'var(--off-white)', padding: '5rem 0' }}>
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Stories of Impact</span>
            <h2>Voices from Our Community</h2>
          </div>
          <div className="grid grid-3">
            {[
              { quote: "ROLF's after-school programme changed my daughter's life. She went from struggling in maths to being top of her class.", name: 'Nomvula M.', role: 'Parent, Soweto', initials: 'NM' },
              { quote: "The digital skills training gave me the confidence to start my own small business. I'm now earning an income and supporting my family.", name: 'Thabo K.', role: 'Programme Graduate', initials: 'TK' },
              { quote: "Volunteering with ROLF showed me the true meaning of community. The team's dedication is absolutely inspiring.", name: 'Sarah vdM.', role: 'Volunteer Coordinator', initials: 'SV' }
            ].map((t, i) => (
              <div key={i} className="scroll-animate testimonial-card" style={{ transitionDelay: `${i * 150}ms` }}>
                <Quote size={32} style={{ color: 'var(--teal)', opacity: 0.3, marginBottom: '1rem' }} />
                <p className="testimonial-quote">"{t.quote}"</p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar">{t.initials}</div>
                  <div>
                    <div className="testimonial-name">{t.name}</div>
                    <div className="testimonial-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="donate-section section">
        <div className="container" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <h2 style={{ color: '#fff', marginBottom: '1rem' }}>Make a Difference Today</h2>
          <p style={{ color: 'rgba(255,255,255,0.8)', maxWidth: '560px', margin: '0 auto 2rem', fontSize: '1.125rem' }}>
            Every donation, no matter the size, helps us continue our mission of empowering communities.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <button className="btn btn-primary btn-lg" onClick={() => navigate('/donate')}>
              <Heart size={18} /> Donate Now
            </button>
            <button className="btn btn-lg" onClick={() => navigate('/contact')}
              style={{ background: 'transparent', color: '#fff', border: '2px solid rgba(255,255,255,0.3)' }}>
              Get Involved <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      <NewsletterSection />
    </>
  );
};

export default HomePage;
